import type React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";

import type { ContentAPI } from "generated/types";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { PRODUCT_PAGES_ROUTES } from "constants/routes";
import { useBetaEdifyModule } from "hooks/useBetaEdifyModule";

import { ParagraphLarge } from "styles/elements/Texts";
import { CssLink } from "styles/helpers/layout";
import type { ElearningThemeInterface } from "themes/elearningInterface.theme";

import { BaseModuleCard } from "../BaseModuleCard/BaseModuleCard";

interface EdifyModuleCardProps {
  productId: number;
  edifyModule: ContentAPI.EdifyModuleListItem;
}

export const EdifyModuleCard: React.FC<EdifyModuleCardProps> = ({ productId, edifyModule }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("homepage");
  const hogesTheme = useTheme() as ElearningThemeInterface;

  const { isBetaEdifyModule, getBetaEdifyModuleFeedbackFormUrl } = useBetaEdifyModule();

  // Check whether module shall be in beta based on ConfigCat variable
  const isBetaModule = isBetaEdifyModule(edifyModule.packageId);
  const feedbackFormUrl = isBetaModule
    ? getBetaEdifyModuleFeedbackFormUrl(edifyModule.packageId)
    : null;

  const goToEdifyModuleDetails = () => {
    navigate(
      buildUrlWithPathParams(PRODUCT_PAGES_ROUTES.PRODUCT_EDIFY_MODULE_DETAILS, {
        productId,
        packageId: edifyModule.packageId,
      }),
    );
  };

  return (
    <BaseModuleCard
      isProgresslessModule={true}
      dataCy="edify-module-card"
      moduleId={edifyModule.packageId}
      moduleTitle={edifyModule.title}
      moduleDescription={edifyModule.description || ""}
      progresslessModuleIcon={isBetaModule ? "far fa-flask" : undefined}
      progresslessModuleBgColor={isBetaModule ? hogesTheme.palette.yellow._900 : undefined}
      extraCardContent={
        isBetaModule && (
          <ParagraphLarge data-cy="beta-module-note" fontWeight="bold">
            {t("studentDashboard.currentProgressSection.moduleCard.betaContentNote")}
            {feedbackFormUrl && (
              <>
                {" "}
                <Trans
                  components={{
                    feedbackFormLink: (
                      <CssLink
                        external
                        dataCy="beta-feedback-form"
                        to={feedbackFormUrl}
                        onClick={(e) => e?.stopPropagation()}
                      />
                    ),
                  }}
                  i18nKey="studentDashboard.currentProgressSection.moduleCard.provideFeedback"
                  t={t}
                />
              </>
            )}
          </ParagraphLarge>
        )
      }
      actionButtonVariant="primary"
      actionButtonLabel={t("studentDashboard.currentProgressSection.moduleCard.continueButton")}
      onCardClick={goToEdifyModuleDetails}
      onActionButtonClick={goToEdifyModuleDetails}
    />
  );
};
